import { fetchClient } from "@/lib/fetchClient";
import type { MutationConfig } from "@/lib/react-query";
import { queryClient } from "@/lib/react-query";
import { useMutation } from "react-query";
import { isObject } from "../../../utils/typeGuard";

// MenuItemTypeをenumとして定義
export const MenuItemType = {
  FOLDER: "folder",
  WORKFLOW: "workflow",
} as const;

export type MenuItemType = (typeof MenuItemType)[keyof typeof MenuItemType];

type MoveMenuItemRequest = {
  item_id: string;
  item_type: MenuItemType;
  order: number;
  parent_folder_id?: string | null;
};

// バリデーション関数
const validateItemType = (itemType: MenuItemType): boolean => {
  return itemType === MenuItemType.FOLDER || itemType === MenuItemType.WORKFLOW;
};

async function updateMenuItemOrder(params: MoveMenuItemRequest) {
  if (!validateItemType(params.item_type)) {
    throw new Error("item_typeはfolderまたはworkflowである必要があります");
  }

  await fetchClient.PUT("/menuItems", {
    body: params,
  });
}

type UseUpdateMenuItemOrderOptions = {
  config?: MutationConfig<typeof updateMenuItemOrder>;
};

export const useUpdateMenuItemOrder = ({
  config,
}: UseUpdateMenuItemOrderOptions = {}) => {
  return useMutation({
    onMutate: async (_) => {
      // 関連するクエリの取得を一時停止
      await queryClient.cancelQueries("Workflows");
      await queryClient.cancelQueries("folders");
    },
    onError: (_, __, context: unknown) => {
      // エラー時に以前のデータを復元
      if (isObject(context) && "previousWorkflows" in context) {
        queryClient.setQueryData("Workflows", context["previousWorkflows"]);
      }
      if (isObject(context) && "previousFolders" in context) {
        queryClient.setQueryData("folders", context["previousFolders"]);
      }
    },
    onSuccess: async () => {
      // 成功時に関連するすべてのクエリを更新
      await Promise.all([
        queryClient.invalidateQueries("Workflows"),
        queryClient.invalidateQueries("folders"),
        queryClient.invalidateQueries("unassignedWorkflows"),
      ]);
    },
    ...config,
    mutationFn: updateMenuItemOrder,
  });
};
